import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Building2, Plus, Pencil, MapPin, Users, Activity, ShieldCheck,
  Power, RotateCcw, Trash2, AlertTriangle, Archive,
} from "lucide-react";

/**
 * Group Homes management.
 *
 * Permissions (frontend gates, backed by API + DB):
 *   - Add / Edit:            CEO, Project Manager, Administration
 *   - Archive / Restore /
 *     Permanent Delete:      CEO, Project Manager
 *
 * Archived homes are hidden from every active workflow (Score Entry, QA,
 * Reports, Leaderboard, Staff Directory, Dashboard). Historical data is
 * preserved and remains queryable via the Archived tab and existing reports.
 * Permanent deletion is refused by the API if ANY linked record exists.
 */
export default function HomesPage() {
  const { user } = useAuth();
  const [homes, setHomes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active"); // active | archived
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null); // {mode, home}

  const canEdit = ["CEO", "Project Manager", "Administration"].includes(user.access_role);
  const canArchive = ["CEO", "Project Manager"].includes(user.access_role);
  const canPermanentDelete = ["CEO", "Project Manager"].includes(user.access_role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, u] = await Promise.all([
        api.get("/homes", { params: { include_metrics: true, include_archived: true } }),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setHomes(h.data);
      setManagers(u.data.filter((x) => x.access_role === "House Manager" && x.active_status));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = homes.filter((h) => h.active_status);
  const archived = homes.filter((h) => !h.active_status);
  const visible = tab === "active" ? active : archived;

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/homes", creating);
      toast.success(`Home "${creating.home_name}" created`);
      setCreating(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create home");
    }
  };
  const onSave = async (e) => {
    e.preventDefault();
    try {
      const { id, ...rest } = editing;
      await api.patch(`/homes/${id}`, rest);
      toast.success("Home updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update home");
    }
  };
  const onArchive = async (home) => {
    setConfirming({ mode: "archive", home });
  };
  const onRestore = async (home) => {
    try {
      await api.post(`/homes/${home.id}/reactivate`);
      toast.success(`"${home.home_name}" restored to active workflows`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore home");
    }
  };
  const onPermanentDelete = async (home) => {
    try {
      const { data } = await api.get(`/homes/${home.id}/linked-counts`);
      setConfirming({ mode: "permanent", home, linked: data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check linked records");
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">Network</p>
          <h1 className="text-h1 mt-1">Group Homes</h1>
          <p className="text-[15px] text-slate-500 mt-1.5">
            Create homes, assign House Managers, and manage archives.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setCreating(blankHome())}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm"
            data-testid="new-home-btn"
          >
            <Plus size={15} /> New Home
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="homes-tabs">
        <TabButton active={tab === "active"} onClick={() => setTab("active")} testId="tab-active">
          Active <span className="ml-1.5 text-xs opacity-70">{active.length}</span>
        </TabButton>
        <TabButton active={tab === "archived"} onClick={() => setTab("archived")} testId="tab-archived">
          <Archive size={12} className="inline mr-1" />
          Archived <span className="ml-1.5 text-xs opacity-70">{archived.length}</span>
        </TabButton>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`homes-grid-${tab}`}>
          {visible.map((h) => (
            <HomeCard
              key={h.id || h.home_name}
              home={h}
              canEdit={canEdit}
              canArchive={canArchive}
              canPermanentDelete={canPermanentDelete}
              onEdit={() => setEditing({ ...h })}
              onArchive={() => onArchive(h)}
              onRestore={() => onRestore(h)}
              onPermanentDelete={() => onPermanentDelete(h)}
            />
          ))}
          {visible.length === 0 && (
            <p className="text-sm text-slate-500 col-span-full" data-testid="homes-empty">
              {tab === "active" ? "No active homes yet." : "No archived homes."}
            </p>
          )}
        </div>
      )}

      {creating && (
        <Modal title="New Home" onClose={() => setCreating(null)}>
          <HomeForm value={creating} setValue={setCreating} managers={managers} onSubmit={onCreate} submitText="Create" />
        </Modal>
      )}
      {editing && (
        <Modal title={`Edit ${editing.home_name}`} onClose={() => setEditing(null)}>
          <HomeForm value={editing} setValue={setEditing} managers={managers} onSubmit={onSave} submitText="Save" />
        </Modal>
      )}
      {confirming && (
        <ConfirmModal
          state={confirming}
          onClose={() => setConfirming(null)}
          onCompleted={() => { setConfirming(null); load(); }}
        />
      )}
    </div>
  );
}

const blankHome = () => ({ home_name: "", address: "", capacity: 0, house_manager_id: "", active_status: true });

function TabButton({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
        active ? "bg-white text-sky-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function HomeCard({ home, canEdit, canArchive, canPermanentDelete, onEdit, onArchive, onRestore, onPermanentDelete }) {
  const isActive = !!home.active_status;
  return (
    <div
      className={`nv-card p-5 flex flex-col gap-3 ${!isActive ? "bg-slate-50 border-dashed border-slate-300" : ""}`}
      data-testid={`home-card-${home.home_name}`}
    >
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-500"
        }`}>
          <Building2 size={18} />
        </div>
        <div className="flex items-center gap-1.5">
          {canEdit && isActive && (
            <button onClick={onEdit} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Edit" data-testid={`edit-home-${home.home_name}`}>
              <Pencil size={14} />
            </button>
          )}
          {canArchive && isActive && (
            <button onClick={onArchive} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Archive" data-testid={`archive-home-${home.home_name}`}>
              <Power size={14} />
            </button>
          )}
          {canArchive && !isActive && (
            <button onClick={onRestore} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Restore to active" data-testid={`restore-home-${home.home_name}`}>
              <RotateCcw size={14} />
            </button>
          )}
          {canPermanentDelete && !isActive && (
            <button onClick={onPermanentDelete} className="p-1.5 rounded hover:bg-rose-50 text-rose-700" title="Permanent delete" data-testid={`delete-home-${home.home_name}`}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-lg font-bold text-slate-900">{home.home_name}</h3>
          {!isActive && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 text-slate-600 border border-slate-300 px-2 py-0.5 text-[11.5px] font-bold uppercase tracking-wider">
              <Archive size={10} /> Archived
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
          <MapPin size={12} /> {home.address || "—"}
        </p>
      </div>
      <hr className="border-slate-100" />
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Stat label="Capacity" value={home.capacity || 0} />
        <Stat label="Manager" value={home.manager_name || "Unassigned"} />
        <Stat label="Staff" icon={Users} value={home.staff_count ?? 0} />
        <Stat label="KPI Avg" icon={Activity} value={home.kpi_average != null ? home.kpi_average.toFixed(2) : "—"} tone="sky" />
        <Stat label="QA Score" icon={ShieldCheck} value={home.qa_score != null ? `${home.qa_score}%` : "—"} tone="emerald" />
        <Stat label="Status" value={isActive ? "Active" : "Archived"} tone={isActive ? "emerald" : "slate"} />
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "slate" }) {
  const tones = { slate: "text-slate-700", sky: "text-sky-700", emerald: "text-emerald-700" };
  return (
    <div>
      <p className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
      <p className={`mt-0.5 font-semibold flex items-center gap-1 ${tones[tone]}`}>
        {Icon && <Icon size={11} />} {value}
      </p>
    </div>
  );
}

function HomeForm({ value, setValue, managers, onSubmit, submitText }) {
  const updateField = (k) => (e) => {
    const v = e.target?.value !== undefined ? e.target.value : e;
    setValue({ ...value, [k]: v });
  };
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="home-form">
      <div>
        <label className="text-small-caps">Home name *</label>
        <input required value={value.home_name} onChange={updateField("home_name")}
          className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 text-sm" data-testid="home-name" />
      </div>
      <div>
        <label className="text-small-caps">Address</label>
        <input value={value.address || ""} onChange={updateField("address")}
          className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 text-sm" data-testid="home-address" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-small-caps">Capacity</label>
          <input type="number" min={0} value={value.capacity || 0}
            onChange={(e) => setValue({ ...value, capacity: parseInt(e.target.value, 10) || 0 })}
            className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 text-sm" data-testid="home-capacity" />
        </div>
        <div>
          <label className="text-small-caps">Status</label>
          <select value={value.active_status ? "true" : "false"}
            onChange={(e) => setValue({ ...value, active_status: e.target.value === "true" })}
            className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 text-sm" data-testid="home-status">
            <option value="true">Active</option><option value="false">Inactive</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-small-caps">House Manager</label>
        <select value={value.house_manager_id || ""} onChange={updateField("house_manager_id")}
          className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 text-sm" data-testid="home-manager">
          <option value="">— Unassigned —</option>
          {managers.map((m) => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
        <button type="submit" className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid="home-submit">
          {submitText}
        </button>
      </div>
    </form>
  );
}

function ConfirmModal({ state, onClose, onCompleted }) {
  const { mode, home, linked } = state;
  const [busy, setBusy] = useState(false);
  const [typedName, setTypedName] = useState("");
  const isArchive = mode === "archive";
  const isPermanent = mode === "permanent";
  const linkedCount = linked?.total ?? 0;
  const canDelete = isPermanent && linkedCount === 0;

  const submit = async () => {
    setBusy(true);
    try {
      if (isArchive) {
        await api.delete(`/homes/${home.id}`);
        toast.success(`"${home.home_name}" archived. Historical data preserved.`);
        onCompleted();
      } else if (isPermanent) {
        if (typedName.trim() !== home.home_name) {
          toast.error(`Type "${home.home_name}" exactly to confirm.`);
          setBusy(false);
          return;
        }
        await api.delete(`/homes/${home.id}/permanent`, { params: { confirm_name: home.home_name } });
        toast.success(`"${home.home_name}" permanently deleted.`);
        onCompleted();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
         onClick={() => !busy && onClose()} data-testid={isArchive ? "archive-confirm-modal" : "permanent-delete-modal"}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          {isArchive ? (
            <><Archive className="text-amber-600" size={22} /><h2 className="font-heading text-xl font-bold text-slate-900">Archive Home?</h2></>
          ) : (
            <><AlertTriangle className="text-rose-600" size={22} /><h2 className="font-heading text-xl font-bold text-slate-900">Permanently Delete?</h2></>
          )}
        </div>

        {isArchive && (
          <>
            <p className="text-sm text-slate-700 leading-relaxed">
              Archive <strong className="text-slate-900">{home.home_name}</strong>? It will be hidden from all active workflows (Score Entry, QA Audit, Reports, Leaderboard, Staff Directory, Dashboard) and unavailable for new staff assignments. All historical KPI scores, QA audits, staff history, and reports stay accessible in the Archived tab.
            </p>
            <p className="text-xs text-slate-500 mt-2">You can restore this home at any time.</p>
          </>
        )}

        {isPermanent && !canDelete && (
          <>
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-900">
              <p className="font-semibold">Permanent delete is not available.</p>
              <p className="mt-1"><strong>{home.home_name}</strong> has <strong>{linkedCount}</strong> linked record{linkedCount === 1 ? "" : "s"}. Deleting it would orphan historical KPI scores, QA audits, and staff records — so this operation is refused.</p>
              {linked?.counts && (
                <ul className="mt-2 list-disc ml-5 text-xs space-y-0.5">
                  {Object.entries(linked.counts).map(([k, v]) => (
                    <li key={k}><strong>{v}</strong> {k.replaceAll("_", " ")}</li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-sm text-slate-700 mt-3">
              Keep the historical data safe by archiving instead. The home will disappear from active workflows and remain restorable.
            </p>
          </>
        )}

        {isPermanent && canDelete && (
          <>
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-900">
              <p className="font-semibold">This action cannot be undone.</p>
              <p className="mt-1"><strong>{home.home_name}</strong> has no linked records and can be permanently deleted from the database.</p>
            </div>
            <label className="block mt-4">
              <span className="text-small-caps">Type <code className="bg-slate-100 px-1 rounded text-[12.5px]">{home.home_name}</code> to confirm</span>
              <input
                autoFocus
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={home.home_name}
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-rose-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                data-testid="permanent-delete-name-input"
              />
            </label>
          </>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="confirm-cancel">Cancel</button>
          {isPermanent && !canDelete ? (
            <button
              onClick={async () => { setBusy(true); try { await api.delete(`/homes/${home.id}`); toast.success(`"${home.home_name}" archived.`); onCompleted(); } catch (err) { toast.error(err?.response?.data?.detail || "Archive failed"); } finally { setBusy(false); } }}
              disabled={busy}
              className="px-4 py-2 rounded-md text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              data-testid="switch-to-archive-btn"
            >
              {busy ? "Archiving…" : "Archive Instead"}
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={busy || (isPermanent && typedName.trim() !== home.home_name)}
              className={`px-4 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-50 ${
                isArchive ? "bg-amber-600 hover:bg-amber-700" : "bg-rose-700 hover:bg-rose-800"
              }`}
              data-testid="confirm-submit"
            >
              {busy ? "Working…" : (isArchive ? "Archive Home" : "Permanently Delete")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="home-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
